import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import supabase from '../services/supabase';
import { clearSession as clearExamSession } from '../services/examApiClient';
import { wisAlleConcepten } from '../utils/wizardConcept';
import { docentProfielGeblokkeerd, isAccountGedeactiveerd, naarLoginGedeactiveerd } from '../utils/accountGedeactiveerd';

// Create AuthContext
const AuthContext = createContext();

// Voorgerenderde publieke pagina's (zie reactSnap.include in package.json).
export const PUBLIEKE_PADEN = [
  '/hoe-werkt-het', '/veelgestelde-vragen', '/demo', '/contact', '/check', '/privacy',
  '/voorwaarden', '/subverwerkers', '/login', '/over-ons', '/kennisbank', '/download',
  '/werken-bij', '/en/careers', '/digitaal-toetsen',
];

// Ingangen voor leerlingen (en de uitnodiging voor nieuwe docenten). Geen van
// deze pagina's leest de docent-sessie uit deze context (useAuth); ze hebben
// hun eigen sessie (sessietoken of StudentAuthContext). StudentLogin leest
// useAuth alleen om een al ingelogde docent door te sturen, en wacht daar zelf
// op. Op een gedeeld schoolapparaat met een verlopen docent-sessie en een
// haperende auth-server bleven ze anders ruim 30 s leeg (bevinding 62,
// 19-10-2026). AuthContext.leerlingpaden.test.js bewaakt dat er geen useAuth
// in hun importboom komt.
export const LEERLING_INGANGEN = [
  '/student-login', '/leerling', '/leerling-start', '/leerling-rooster',
  '/communicatie-start', '/toets', '/toets-bedankt', '/communicatie-resultaat',
  '/inzage', '/les', '/examentraining-login', '/uitnodiging/demo-2026',
];

/** Mag deze pagina tonen voordat de docent-sessie bekend is? */
export function wachtNietOpSessie(pathname) {
  if (pathname === '/') return true;
  return [...PUBLIEKE_PADEN, ...LEERLING_INGANGEN]
    .some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Custom hook om AuthContext te gebruiken
export function useAuth() {
  return useContext(AuthContext);
}

// AuthProvider component
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [docentProfile, setDocentProfile] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const vorigeGebruikerRef = useRef(null);

  // Haal docent profiel op uit Supabase, incl. de gekoppelde school (voor feature-flags).
  async function fetchDocentProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('docenten')
        .select('*, scholen(id, naam, brin_code, features, licentie_type)')
        .eq('id', userId)
        .maybeSingle();

      // Gedeactiveerd door de school (is_actief = false) of soft-verwijderd
      // (Bewaarbeleid §3.2): direct uitloggen en naar /login met een melding.
      // Na migratie 104 weigert de database het verzoek zelf (403
      // ACCOUNT_GEDEACTIVEERD); de rij-controle dekt de tijd ervoor.
      if (isAccountGedeactiveerd(error) || docentProfielGeblokkeerd(data)) {
        setDocentProfile(null);
        setSchool(null);
        await supabase.auth.signOut();
        naarLoginGedeactiveerd();
        return null;
      }

      if (error) throw error;

      setDocentProfile(data);
      setSchool(data?.scholen ?? null);
      return data;
    } catch (err) {
      // Docent profiel niet gevonden — kan een leerling zijn
      setDocentProfile(null);
      setSchool(null);
      return null;
    }
  }

  // Logout docent
  async function logout() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  // Voeg .uid toe als alias voor .id (compatibiliteit met bestaande code)
  function withUidCompat(user) {
    if (!user) return null;
    return { ...user, uid: user.id };
  }

  // Luister naar auth state changes
  useEffect(() => {
    // Check huidige sessie bij mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user || null;
      setCurrentUser(withUidCompat(user));
      if (user) {
        fetchDocentProfile(user.id);
      }
      setLoading(false);
    }).catch((err) => {
      console.error('AuthContext: getSession() failed:', err);
      setCurrentUser(null);
      setDocentProfile(null);
      setLoading(false);
    });

    // Luister naar toekomstige auth wijzigingen
    // BELANGRIJK: fetchDocentProfile NIET awaiten hier!
    // Supabase JS v2 emits onAuthStateChange vanuit _initialize() die een
    // navigator.lock vasthoudt. Als we hier await doen op een Supabase query,
    // wacht die query op getSession() → die wacht op _initialize() → DEADLOCK.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const user = session?.user || null;
        // Uitloggen of een ander account in dit tabblad: concepten van de
        // toetswizard weg, zodat de volgende docent ze niet ziet (15-10-2026).
        if (!user || (vorigeGebruikerRef.current && vorigeGebruikerRef.current !== user.id)) wisAlleConcepten();
        vorigeGebruikerRef.current = user?.id || null;
        setCurrentUser(withUidCompat(user));

        if (user) {
          fetchDocentProfile(user.id);
        } else {
          setDocentProfile(null);
          setSchool(null);
          clearExamSession(); // Clear exam session token on logout
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Direct na een geslaagde inlog met e-mail (LoginNew). Normaal komt de
  // gebruiker binnen via onAuthStateChange, maar supabase-js houdt SIGNED_IN
  // vast zolang initialize() nog loopt. Met een verlopen oude sessie en een
  // haperende auth-server duurt dat tot ~30 s, en dan leek inloggen niets te
  // doen (bevinding 61, 19-10-2026). Het latere SIGNED_IN zet dezelfde
  // gebruiker nog een keer; dat kan geen kwaad.
  function meldIngelogd(user) {
    if (!user) return;
    if (vorigeGebruikerRef.current && vorigeGebruikerRef.current !== user.id) wisAlleConcepten();
    vorigeGebruikerRef.current = user.id;
    setCurrentUser(withUidCompat(user));
    fetchDocentProfile(user.id);
    setLoading(false);
  }

  // Refresh docent profiel (na vakken update etc.)
  async function refreshDocentProfile() {
    if (currentUser) {
      return fetchDocentProfile(currentUser.id);
    }
    return null;
  }

  const value = {
    currentUser,
    docentProfile,
    school,
    loading,
    logout,
    refreshDocentProfile,
    meldIngelogd
  };

  // Publieke pagina's hebben de sessie alleen nodig voor de knop Inloggen/Dashboard.
  // Niet op de sessie wachten: met een verlopen sessie of een trage auth-server
  // bleef de pagina anders seconden lang leeg (17-10-2026 kennisbank, 18-10 alle
  // publieke pagina's, 20-10 de leerling-ingangen).
  const { pathname } = useLocation();
  const nietWachten = wachtNietOpSessie(pathname);

  return (
    <AuthContext.Provider value={value}>
      {(!loading || nietWachten) && children}
    </AuthContext.Provider>
  );
}
